import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, Pill, UtensilsCrossed, Globe, Scale, FileText, Mic, ScanLine } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const SCENARIOS = [
  { id: "mail", icon: Mail, label: "Scanning mail", result: "“Letter from Pacific Gas & Electric — your bill of $86.40 is due July 3rd.”" },
  { id: "prescription", icon: Pill, label: "Reading prescriptions", result: "“Take one tablet by mouth twice daily with food. Refills remaining: 2.”" },
  { id: "menu", icon: UtensilsCrossed, label: "Reading restaurant menus", result: "“Mains: grilled salmon $24, ribeye $31, roasted vegetable risotto $19…”" },
  { id: "web", icon: Globe, label: "Reading websites", result: "“Headline: City council approves new accessibility funding. Reading article…”" },
  { id: "legal", icon: Scale, label: "Explaining legal paperwork", result: "Plain English: “This renews your lease for 12 months. Rent stays the same.”" },
  { id: "pdf", icon: FileText, label: "Reading PDFs", result: "“Page 1 of 12 — Annual Benefits Summary. Reading section one…”" },
  { id: "voice", icon: Mic, label: "Voice commands", result: "You said: “Explain this.” VoiceLens is simplifying the document now…" },
];

export default function Showcase() {
  const [active, setActive] = useState(SCENARIOS[0]);

  return (
    <section id="showcase" className="relative bg-[#050F1F]/50 py-24 lg:py-32" aria-labelledby="showcase-heading">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <span id="showcase-heading" className="sr-only">VoiceLens AI feature showcase</span>
        <SectionHeading
          eyebrow={{ index: "04", label: "Feature Showcase" }}
          title={<>One app. <span className="text-glow-gradient">Every word in reach.</span></>}
          sub="Tap a scenario and watch how VoiceLens handles the reading moments that fill your day."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-[340px_1fr]">
          <Reveal>
            <ul className="grid grid-cols-2 gap-3 lg:grid-cols-1" role="tablist" aria-label="Showcase scenarios">
              {SCENARIOS.map((s) => {
                const Icon = s.icon;
                const selected = active.id === s.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      data-testid={`showcase-tab-${s.id}`}
                      onClick={() => setActive(s)}
                      className={`flex min-h-[56px] w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition-all duration-300 sm:text-base ${
                        selected
                          ? "border-[#00D4FF]/60 bg-[#00D4FF]/12 text-white shadow-[0_0_20px_rgba(0,212,255,0.18)]"
                          : "border-slate-500/25 bg-[#0C2340]/50 text-slate-300 hover:border-[#00D4FF]/35 hover:text-white"
                      }`}
                    >
                      <Icon className={`h-5 w-5 shrink-0 ${selected ? "text-[#00D4FF]" : "text-slate-400"}`} aria-hidden="true" />
                      {s.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              className="glass-panel card-glow relative flex h-full min-h-[380px] flex-col overflow-hidden rounded-3xl p-7 sm:p-10"
              role="tabpanel"
              data-testid="showcase-panel"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35 }}
                  className="flex h-full flex-col"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#2563EB] to-[#00D4FF]">
                      <active.icon className="h-5 w-5 text-white" aria-hidden="true" />
                    </span>
                    <h3 className="font-display text-lg font-bold text-white sm:text-xl">{active.label}</h3>
                  </div>

                  <div className="relative mt-7 flex-1 overflow-hidden rounded-2xl border border-[#00D4FF]/18 bg-[#050F1F] p-6" aria-hidden="true">
                    <div className="space-y-3">
                      {[88, 100, 72, 94, 60, 82, 48].map((w, i) => (
                        <div key={i} className="h-2.5 rounded-full bg-slate-400/20" style={{ width: `${w}%` }} />
                      ))}
                    </div>
                    <div className="scan-beam-line absolute left-4 right-4 h-[3px] rounded-full bg-gradient-to-r from-transparent via-[#00D4FF] to-transparent shadow-[0_0_16px_#00D4FF]" />
                  </div>

                  <div className="mt-6 flex items-start gap-3 rounded-2xl border border-[#00D4FF]/20 bg-[#00D4FF]/8 p-5">
                    <ScanLine className="mt-0.5 h-5 w-5 shrink-0 text-[#00D4FF]" aria-hidden="true" />
                    <p className="text-base leading-relaxed text-slate-100" data-testid="showcase-result-text">
                      {active.result}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
